import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment, { Moment } from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { IAllowedInputDates } from '../../../../context/report-periods/report-periods.model';
import CapitalGoodService from '../../../../shared/services/capital-good/capital-good-service';
import { ICapitalGood } from '../../../../shared/services/capital-good/capital-good.model';
import { IEditFileUploadLineGoodEmission, IFileUploadLineGoodEmission } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../../shared/utils/subsidiary-utils';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import { formatTransaction } from '../../../../shared/utils/format-post-utils';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { EmissionsInputAdvanced } from '../../../presentation-components/emissons-components/overview/emissions-input/emissions-input-advanced-form';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';

export const EditUploadLineGoodEmissionDialog: FunctionComponent<{
  editUploadLine: IFileUploadLineGoodEmission,
  open: boolean,
  closeHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineGoodEmission) => void,
  isSubmitting: boolean,
}> = ({
  editUploadLine,
  open,
  closeHandler,
  submitHandler,
  isSubmitting, }) => {
    //Services
    const capitalGoodService = new CapitalGoodService()
    const subsidiaryService = SubsidiaryService.getInstance()
    const customFieldService = new CustomFieldService()
    //Context
    const userDetailsContext = useContext(UserDetailsContext)
    const organisationDetails = userDetailsContext.userDetails?.organisation
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const reportPeriodContext = useContext(ReportPeriodContext)
    //State
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    const [capitalGoods, setCapitalGoods] = useState<ICapitalGood[]>()
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [openStartDate, setOpenStartDate] = useState(false);
    const [allowedDates, setAllowedDates] = useState<IAllowedInputDates>({ startDate: undefined, endDate: undefined })
    //Use Effects
    useEffect(() => {
      if (editUploadLine.reportPeriodId && reportPeriodContext.allReportPeriods) {
        const reportPeriod = reportPeriodContext.allReportPeriods.find(rp => rp.id === editUploadLine.reportPeriodId)
        if (reportPeriod) {
          setAllowedDates({
            startDate: reportPeriod.startDate,
            endDate: reportPeriod.endDate,
          })
        }
      }
    }, [editUploadLine.reportPeriodId, reportPeriodContext.allReportPeriods])

    useEffect(() => {
      formik.setValues({
        capitalGoodId: editUploadLine.capitalGoodId,
        unitsConsumed: editUploadLine.unitsConsumed,
        isFlag: editUploadLine.isFlag === true,
        flagCategory: editUploadLine.flagCategory ?? '',
        startDate: editUploadLine.startDate,
        subsidiaryId: editUploadLine.subsidiaryId,
        countryId: editUploadLine.countryId,
        projectId: editUploadLine.projectId,
        locationId: editUploadLine.locationId,
        vehicleId: editUploadLine.vehicleId,
        contactSupplierId: editUploadLine.contactSupplierId,
      })

      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
        })
        .catch(err => console.log(err))

      capitalGoodService.getAllActive()
        .then(response => {
          setCapitalGoods(response)
        })
        .catch(err => console.log(err))

      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => console.log(err))
    }, [])

    useEffect(() => {
      if (customFields && customFields.length && editUploadLine?.customFieldValues) {
        customFields.forEach(field => {
          const matchingValue = editUploadLine.customFieldValues.find(v => v.customFieldId === field.id)?.value ?? '';
          formik.setFieldValue(field.id, matchingValue, false);
        });
      }
    }, [customFields, editUploadLine]);
    
    //Formik
    const editUploadSchema = Yup.object().shape({
      capitalGoodId: Yup.string().required(`Name of ${screenLabelsContext.getLabel('product')} is required`),
      unitsConsumed: Yup.number().min(0.01, 'The amount must be greater than 0').required('You must provide an amount'),
      startDate: Yup.string().required('Purchased date is required'),
      subsidiaryId: Yup.string().required(`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} is required`),
      countryId: Yup.string().required('Country is required'),
      projectId: Yup.string().nullable(),
      locationId: Yup.string().nullable(),
      vehicleId: Yup.string().nullable(),
      contactSupplierId: Yup.string().nullable(),
    })

    const formik = useFormik({
      initialValues: {
        capitalGoodId: editUploadLine.capitalGoodId,
        unitsConsumed: editUploadLine.unitsConsumed,
        isFlag: editUploadLine.isFlag === true,
        flagCategory: editUploadLine.flagCategory ?? '',
        startDate: editUploadLine.startDate,
        subsidiaryId: editUploadLine.subsidiaryId,
        countryId: editUploadLine.countryId,
        projectId: editUploadLine.projectId,
        locationId: editUploadLine.locationId,
        vehicleId: editUploadLine.vehicleId,
        contactSupplierId: editUploadLine.contactSupplierId,
      },
      validationSchema: editUploadSchema,
      onSubmit: (values) => {
        const response = formatTransaction(values, '', true, customFields, '')
        submitHandler(editUploadLine.id, response)
      }
    });

    //Formik Event Handlers
    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setFieldValue(name, date.format('YYYY-MM-DD'));
      }
    }

    const subsidiaryHandler = (e: SelectChangeEvent<string>) => {
      const subsidiary = subsidiaries?.find(s => s.id === e.target.value)
      if (subsidiary) {
        formik.setFieldValue('subsidiaryId', subsidiary.id)
        formik.setFieldValue('countryId', subsidiary.countryId)
      }
    }

    //Component Event Handlers
    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form'>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            Edit Uploaded Data
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='capitalGoodId'>{screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })}</InputLabel>
              <Select
                id='capitalGoodId' label={screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} name='capitalGoodId'
                value={formik.values.capitalGoodId || ''}
                onChange={formik.handleChange}
                error={formik.touched.capitalGoodId && Boolean(formik.errors.capitalGoodId)}
                sx={{ color: 'text.primary', }}>
                {capitalGoods?.sort((a, b) => a.name.localeCompare(b.name)).map((good, index) =>
                  <MenuItem key={index} value={good.id}>{good.name}</MenuItem>
                )}
              </Select>
              {formik.touched.capitalGoodId && formik.errors.capitalGoodId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.capitalGoodId}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='unitsConsumed'>Number of Units Purchased</InputLabel>
              <OutlinedInput inputProps={{ min: 0 }} id='unitsConsumed' label='Number of Units Purchased' name='unitsConsumed' type='number'
                value={formik.values.unitsConsumed || ''}
                onChange={formik.handleChange}
                error={formik.touched.unitsConsumed && Boolean(formik.errors.unitsConsumed)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.unitsConsumed && formik.errors.unitsConsumed &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.unitsConsumed}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label='Date Purchased'
                  value={formik.values.startDate ? moment(formik.values.startDate) : null}
                  onChange={(date) => dateHandler(date, 'startDate')}
                  format='DD/MM/YYYY'
                  maxDate={moment(allowedDates.endDate)}
                  minDate={moment(allowedDates.startDate)}
                  sx={{ color: 'text.primary' }}
                  open={openStartDate}
                  onOpen={() => setOpenStartDate(true)}
                  onClose={() => setOpenStartDate(false)}
                  slotProps={{
                    textField: {
                      onClick: () => setOpenStartDate(true),
                      error: formik.touched.startDate && Boolean(formik.errors.startDate)
                    },
                  }}
                />
              </LocalizationProvider>
              {formik.touched.startDate && formik.errors.startDate &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.startDate}</FormHelperText>
              }
            </FormControl>
            {subsidiaries && (organisationDetails?.enableSubsidiaries || subsidiaries.length > 1) &&
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='subsidiaryId'>{screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}</InputLabel>
                <Select
                  id='subsidiaryId' label={screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} name='subsidiaryId'
                  value={formik.values.subsidiaryId || ''}
                  onChange={subsidiaryHandler}
                  error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                  sx={{ color: 'text.primary' }}>
                  {getSelectableSubsidiaries(subsidiaries, formik.values.subsidiaryId).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                    <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                  )}
                </Select>
                {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.subsidiaryId ? <div>{formik.errors.subsidiaryId as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>}
            {customFields &&
              <EmissionsInputAdvanced
                isEdit={true}
                customFields={customFields}
                formik={formik}
                hideRegulatedEmissionTradingScheme={true}
                hideCustomFields={false}
                hideRec={true}
                hideCustomBasedMarketMultiplier={true}
                hideEnergySupplierTariff={true}
                hideExcludedFromReporting={true}
              />}
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog>
    )
  }